export function buildSafeSameOriginRedirectTarget(targetPath = '/') {
  const fallbackUrl = new URL('/', window.location.origin).toString();

  try {
    const parsedUrl = new URL(String(targetPath || '/'), window.location.origin);
    return parsedUrl.origin === window.location.origin ? parsedUrl.toString() : fallbackUrl;
  } catch (error) {
    console.warn('Unable to build same-origin redirect target:', error);
    return fallbackUrl;
  }
}

const LOGIN_DASHBOARD_ORIGINS = {
  'https://auth.hermetiq.io': 'https://dashboard.hermetiq.io',
  'https://auth-dev.hermetiq.dev': 'https://web-dev.hermetiq.dev',
};

export function buildLoginOriginDashboardRedirectTarget(targetPath, loginOrigin = window.location.origin) {
  const dashboardOrigin = LOGIN_DASHBOARD_ORIGINS[loginOrigin];
  if (!dashboardOrigin) return null;

  try {
    const target = new URL(String(targetPath || '/dashboard'), loginOrigin);
    if (target.origin !== loginOrigin) return `${dashboardOrigin}/dashboard`;
    return `${dashboardOrigin}${target.pathname}${target.search}${target.hash}`;
  } catch (error) {
    return `${dashboardOrigin}/dashboard`;
  }
}

// sameOriginPath returns target as a path on origin, or fallback when it is
// missing, anywhere else, carries credentials, or does not parse.
function sameOriginPath(target, origin, fallback) {
  if (!target) return fallback;
  try {
    const url = new URL(String(target), origin);
    const path = `${url.pathname}${url.search}${url.hash}`;
    const local = url.origin === origin && !url.username && !url.password
      && path.startsWith('/') && !path.startsWith('//');
    return local ? path : fallback;
  } catch {
    return fallback;
  }
}

// A newly exchanged Stytch identity must replace the dashboard's oauth2-proxy
// session. That session is the shared .hermetiq.io / .hermetiq.dev cookie
// (OAUTH2_PROXY_COOKIE_DOMAINS), not a dashboard-only one: signing out of the
// dashboard proxy clears it for every Hermetiq proxy that reads it, and each
// signs in again silently through the new SSO session. Never visit the
// login-origin logout endpoint here: it would revoke the SSO session this
// login just created.
//
// The login origin's own /authorize continuation stays as it is, and any other
// bare path names a dashboard page. Everything else fails closed: a
// destination off the dashboard, with credentials, or that does not parse
// enters the dashboard home through the same sign-out.
export function buildFreshDashboardSessionRedirectTarget(target, loginOrigin = window.location.origin) {
  const dashboardOrigin = LOGIN_DASHBOARD_ORIGINS[loginOrigin];
  if (!dashboardOrigin) return sameOriginPath(target, loginOrigin, '/dashboard');
  const home = `${dashboardOrigin}/oauth2/sign_out?rd=%2Fdashboard`;
  const loginPath = sameOriginPath(target, loginOrigin, '');
  if (loginPath && new URL(loginPath, loginOrigin).pathname === '/authorize') return loginPath;
  const dashboardPath = sameOriginPath(target, dashboardOrigin, '');
  return dashboardPath ? `${dashboardOrigin}/oauth2/sign_out?rd=${encodeURIComponent(dashboardPath)}` : home;
}

// Registrable domains trusted as cross-origin redirect targets. The login origin
// (auth-dev.hermetiq.dev / auth.hermetiq.io) sends the browser back to the
// dashboard (web-dev.hermetiq.dev / dashboard.hermetiq.io) after revoking the
// Stytch session at logout -- same-site but cross-origin, which the same-origin
// helpers above reject.
const HERMETIQ_REDIRECT_DOMAINS = ['hermetiq.io', 'hermetiq.dev'];

// buildSafeHermetiqRedirectTarget accepts an https URL under a Hermetiq
// registrable domain and otherwise falls back to a same-origin path. Used for
// the post-logout hop between the login origin and the dashboard; the target
// carries no secrets (the session is already revoked), so a subdomain-level
// allow-list is sufficient to prevent an off-domain open redirect.
export function buildSafeHermetiqRedirectTarget(target, fallbackPath = '/') {
  const fallback = buildSafeSameOriginRedirectTarget(fallbackPath);
  if (!target) return fallback;

  try {
    const parsed = new URL(String(target), window.location.origin);
    if (parsed.protocol !== 'https:') return fallback;
    const host = parsed.hostname.toLowerCase();
    const allowed = HERMETIQ_REDIRECT_DOMAINS.some(
      (domain) => host === domain || host.endsWith(`.${domain}`)
    );
    return allowed ? parsed.toString() : fallback;
  } catch (error) {
    console.warn('Unable to build Hermetiq redirect target:', error);
    return fallback;
  }
}

export function buildSafeSameOriginPathRedirectTarget(targetPath = '/', fallbackPath = '/') {
  const fallbackTarget = String(fallbackPath || '/');
  const fallback = fallbackTarget.startsWith('/') && !fallbackTarget.startsWith('//')
    ? fallbackTarget
    : '/';

  try {
    const parsedUrl = new URL(String(targetPath || fallback), window.location.origin);
    if (parsedUrl.origin !== window.location.origin) {
      return fallback;
    }

    const pathTarget = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    return pathTarget.startsWith('/') && !pathTarget.startsWith('//') ? pathTarget : fallback;
  } catch (error) {
    console.warn('Unable to build same-origin path redirect target:', error);
    return fallback;
  }
}
